import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { trackAeoLondonConversion, trackCalendlyClick } from "@/lib/analytics";
import { isOctoberOfferActive } from "@shared/commercialOffer";

const BOOKING_URL = "https://calendly.com/modielnadi/30min?utm_source=site&utm_campaign=aeo-london";

type ServiceNeeded = "Free AI Visibility Score" | "AI Visibility Audit" | "Foundation" | "Growth" | "Authority" | "Not sure";
type PainPoint = "Not cited in AI answers" | "Traffic falling from AI Overviews" | "Leads low quality" | "No AI-search measurement" | "Other";
type BudgetContext = "Under £3,000" | "£3,000 to £5,000" | "£5,000 to £10,000" | "£10,000+" | "Not set yet";

type FormValues = {
  serviceNeeded: ServiceNeeded | "";
  painPoint: PainPoint | "";
  budget: BudgetContext | "";
  name: string;
  email: string;
  website: string;
  phone: string;
};

const initialValues: FormValues = {
  serviceNeeded: "",
  painPoint: "",
  budget: "",
  name: "",
  email: "",
  website: "",
  phone: "",
};

const fieldClassName = "w-full rounded-lg border border-white/15 bg-slate-950/45 px-3 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/35";
const chipClassName = "min-h-11 rounded-lg border px-3 py-2 text-left text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-[#FF8C00]";

const serviceOptions: readonly { value: ServiceNeeded; label: string }[] = [
  { value: "Free AI Visibility Score", label: "AI Visibility" },
  { value: "AI Visibility Audit", label: isOctoberOfferActive() ? "AI Visibility Audit £750 (£638 until 31 Oct)" : "AI Visibility Audit £750" },
  { value: "Foundation", label: "Foundation £3,000/mo" },
  { value: "Growth", label: "Growth £5,000/mo" },
  { value: "Authority", label: "Authority from £9,500/mo" },
  { value: "Not sure", label: "Not sure" },
];

const painPointOptions: readonly PainPoint[] = [
  "Not cited in AI answers",
  "Traffic falling from AI Overviews",
  "Leads low quality",
  "No AI-search measurement",
  "Other",
];

export default function AeoLondonLeadForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const successRef = useRef<HTMLDivElement>(null);

  const submitLead = trpc.contact.aeoLondonEnquiry.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      setError("");
      trackAeoLondonConversion({ action: "form_success", placement: "action_plan_form" });
      window.setTimeout(() => successRef.current?.focus(), 0);
    },
    onError: (mutationError) => {
      setError("We could not send that enquiry just now. Please try again or book a 30-minute call.");
      trackAeoLondonConversion({ action: "form_error", placement: "action_plan_form" });
      console.error("[AEO London lead form]", mutationError);
    },
  });

  const update = <Field extends keyof FormValues>(field: Field, value: FormValues[Field]) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const markStarted = () => {
    if (started) return;
    setStarted(true);
    trackAeoLondonConversion({ action: "form_start", placement: "action_plan_form" });
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!values.serviceNeeded || !values.painPoint || !values.budget) {
      setError("Please choose a service, main pain point and monthly budget context.");
      return;
    }
    trackAeoLondonConversion({ action: "form_submit", placement: "action_plan_form" });
    submitLead.mutate({
      name: values.name.trim(),
      email: values.email.trim(),
      website: values.website.trim(),
      phone: values.phone.trim() || undefined,
      serviceNeeded: values.serviceNeeded,
      painPoint: values.painPoint,
      budget: values.budget,
    });
  };

  if (submitted) {
    return (
      <div ref={successRef} tabIndex={-1} className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-6 outline-none" aria-live="polite">
        <CheckCircle2 className="size-6 text-emerald-300" aria-hidden="true" />
        <h3 className="mt-3 font-serif text-2xl text-white">Your enquiry has been sent.</h3>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-200">
          We will use the details you provided to review the context and respond to this request. You can also choose a time now if a 30-minute call is the clearest next step.
        </p>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-[#FF8C00] px-5 py-3 text-sm font-bold text-[#111827] transition hover:bg-[#ffad4d] focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:ring-offset-2 focus:ring-offset-[#101722]"
          onClick={() => {
            trackCalendlyClick("aeo_london_form_success");
            trackAeoLondonConversion({ action: "booking_click", placement: "action_plan_form_success" });
          }}
        >
          Book a 30-minute call <ArrowRight className="size-4" aria-hidden="true" />
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} onFocus={markStarted} className="grid gap-6">
      <input type="hidden" name="src" value="aeo-london" />
      <fieldset>
        <legend className="text-sm font-semibold text-white">1. What would be most useful? <span className="text-[#ffad4d]">*</span></legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {serviceOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={values.serviceNeeded === option.value}
              onClick={() => update("serviceNeeded", option.value)}
              className={`${chipClassName} ${values.serviceNeeded === option.value ? "border-[#FF8C00] bg-[#FF8C00]/15 text-white" : "border-white/15 bg-slate-950/30 text-slate-200 hover:border-white/35"}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend className="text-sm font-semibold text-white">2. What is the main pain point? <span className="text-[#ffad4d]">*</span></legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {painPointOptions.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={values.painPoint === option}
              onClick={() => update("painPoint", option)}
              className={`${chipClassName} ${values.painPoint === option ? "border-[#FF8C00] bg-[#FF8C00]/15 text-white" : "border-white/15 bg-slate-950/30 text-slate-200 hover:border-white/35"}`}
            >
              {option}
            </button>
          ))}
        </div>
      </fieldset>

      <Field label="3. Monthly budget context" required htmlFor="aeo-london-budget">
        <select id="aeo-london-budget" required value={values.budget} onChange={(event) => update("budget", event.target.value as BudgetContext)} className={fieldClassName}>
          <option value="">Select a range</option>
          <option value="Under £3,000">Under £3,000</option>
          <option value="£3,000 to £5,000">£3,000 to £5,000</option>
          <option value="£5,000 to £10,000">£5,000 to £10,000</option>
          <option value="£10,000+">£10,000+</option>
          <option value="Not set yet">Not set yet</option>
        </select>
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" required htmlFor="aeo-london-name">
          <input id="aeo-london-name" type="text" autoComplete="name" required value={values.name} onChange={(event) => update("name", event.target.value)} className={fieldClassName} />
        </Field>
        <Field label="Work email" required htmlFor="aeo-london-email">
          <input id="aeo-london-email" type="email" autoComplete="email" required value={values.email} onChange={(event) => update("email", event.target.value)} className={fieldClassName} />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Website URL" required htmlFor="aeo-london-website">
          <input id="aeo-london-website" type="url" inputMode="url" autoComplete="url" placeholder="https://" required value={values.website} onChange={(event) => update("website", event.target.value)} className={fieldClassName} />
        </Field>
        <Field label="Phone (optional)" htmlFor="aeo-london-phone">
          <input id="aeo-london-phone" type="tel" autoComplete="tel" value={values.phone} onChange={(event) => update("phone", event.target.value)} className={fieldClassName} />
        </Field>
      </div>
      <p className="text-xs leading-relaxed text-slate-400">
        By submitting, you ask us to use these details to respond to this enquiry. This is not a newsletter subscription. Read our <a className="text-[#ffad4d] underline-offset-4 hover:underline" href="/privacy-policy/">Privacy Policy</a>.
      </p>
      {error ? <p className="rounded-lg border border-red-400/30 bg-red-400/10 p-3 text-sm text-red-100" role="alert">{error}</p> : null}
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={submitLead.isPending} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#FF8C00] px-5 py-3 text-sm font-bold text-[#111827] transition hover:bg-[#ffad4d] disabled:cursor-not-allowed disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:ring-offset-2 focus:ring-offset-[#101722]">
          {submitLead.isPending ? <><Loader2 className="size-4 animate-spin" aria-hidden="true" /> Sending enquiry</> : <>Request my AEO action plan <ArrowRight className="size-4" aria-hidden="true" /></>}
        </button>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:border-[#FF8C00] hover:text-[#ffad4d] focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:ring-offset-2 focus:ring-offset-[#101722]"
          onClick={() => {
            trackCalendlyClick("aeo_london_action_plan_form");
            trackAeoLondonConversion({ action: "booking_click", placement: "action_plan_form" });
          }}
        >
          Book a 30-minute call
        </a>
      </div>
    </form>
  );
}

function Field({ label, required, htmlFor, children }: { label: string; required?: boolean; htmlFor: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-2 block text-sm font-semibold text-white">
        {label}{required ? <span className="text-[#ffad4d]"> *</span> : null}
      </label>
      {children}
    </div>
  );
}
